import {
  FetchArgs,
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
  BaseQueryFn,
} from "@reduxjs/toolkit/query/react";
import { getIdToken } from "@firebase/auth";
import { auth } from "../firebase/firebase";
import { captureException, addBreadcrumb } from "../sentry.config";
import { getSession } from "./auth";
import { updateOnlineStatus } from "./staticSlice";
import { toast } from "../components/Toast/toast";

interface CustomFetchBaseQueryArgs extends FetchArgs {
  customRedirect?: boolean;
}

const baseQuery = fetchBaseQuery({
  // baseUrl: "https://oogwai.gaggle.online/",
  // baseUrl: "https://analytics.oogw.ai/",
  baseUrl: process.env.REACT_APP_BASE_URL,
  prepareHeaders: async (headers) => {
    // Check if running in iframe
    const isInIframe = window.self !== window.top;

    // If in iframe, use static encoded token for authentication
    if (isInIframe) {
      // Static encoded token for iframe authentication
      // Uses XOR cipher with static key, then base64 encoded
      // Static decode key: "g0jXkz38WCcxtMjS"
      // Original token: "OOGWAI_IFRAME_ACCESS_KEY_N0tAga1n!_"
      // Backend must use the same static key to decode
      const staticToken = "OH8tDy4zTHERFSI1MRIrECR1PQsIMVZxCA1TDDU2C2IJEQk=";
      // headers.set("itc", `${staticToken}`);
      headers.set("email", "karan.anand@interactiveavenues.com")
      return headers;
    }

    // Otherwise, use normal authentication flow
    // Public routes that don't require authentication
    const publicRoutes = [
      "/login",
      "/signup",
      "/verify-email",
      "/enter-referral-code",
      "/about-your-company",
      // "/integration-pereference",
      // "/select-goals",
      // "/setup-workspace",
      "/set-password",
      "/forgot-password",
    ];

    if (!publicRoutes.includes(window.location.pathname)) {
      try {

        const urlParams = new URLSearchParams(window.location.search);
        const shopifyTokenFromUrl = urlParams.get("token")?.replace(/ /g, '+');
        const emailShop = urlParams.get("email");
        const getShopifyUser = localStorage.getItem("oogw.ai_user");
        const storedTokenType = localStorage.getItem("oogw.ai_user_type");

        // Priority 1: Try to get JWT Bearer token from session first
        try {
          const session: any = await getSession();
          const token = session?.idToken?.jwtToken;

          if (token) {
            const getEmail = localStorage.getItem("oogw.ai_shadow_user_email");
            if (getEmail && getEmail !== 'undefined') {
              headers.set("email", getEmail);
            }
            // Always use "Bearer" for JWT tokens
            headers.set("Authorization", `Bearer ${token}`);
          } else {
            throw new Error("No JWT token in session, trying Shopify token");
          }
        } catch (jwtError) {
          // Priority 2: If JWT token doesn't exist, check Shopify token from URL
          if (shopifyTokenFromUrl && emailShop) {
            headers.set("Authorization", `Shopify ${shopifyTokenFromUrl}`);
          }
          // Priority 3: Check if user has Shopify stored in localStorage
          else if (storedTokenType === "Shopify" && getShopifyUser) {
            headers.set("Authorization", `Shopify ${getShopifyUser}`);
          }
          // No valid token found
          else {
            throw new Error("No valid authentication token available");
          }
        }


      } catch (error) {
        // console.error("Token fetch error:", error);
        addBreadcrumb({
          message: 'Token fetch failed',
          category: 'auth',
          level: 'error',
          data: { error: String(error) },
        });
        captureException(error, { context: 'token_fetch' });
        toast.error("Session expired. Please log in again.");
        localStorage.clear();
        window.location.href = "/login";
      }
    }

    return headers;
  }
});


// Custom fetch base query with token handling
const customFetchBaseQuery: BaseQueryFn<
  string | CustomFetchBaseQueryArgs,
  unknown,
  FetchBaseQueryError,
  {},
  FetchBaseQueryMeta
> = async (args: any, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  // Check if response is HTML instead of JSON
  if (result?.data && typeof result.data === 'string') {
    // Check if the response looks like HTML
    const isHTML = /<[^>]+>/.test(result.data as string);
    if (isHTML) {
      // console.error('Received HTML response instead of JSON:', result.data);
      addBreadcrumb({
        message: 'Received HTML response instead of JSON',
        category: 'api',
        level: 'error',
        data: {
          url: typeof args === 'string' ? args : args.url,
          responseSnippet: (result.data as string).substring(0, 200),
        },
      });
      captureException(new Error('HTML response instead of JSON'), {
        url: typeof args === 'string' ? args : args.url,
        response: (result.data as string).substring(0, 500),
      });
      toast.error("Something went wrong. Please try again.", {
        toastId: "html-response-error",
      });
      // Convert to an error result
      return {
        error: {
          status: 'PARSING_ERROR',
          error: 'Received HTML response instead of JSON',
          data: result.data
        } as FetchBaseQueryError
      };
    }
  }

  if (result?.error?.status === 401) {
    // Check if running in iframe
    const isInIframe = window.self !== window.top;

    // If in iframe, just log the error (static token is invalid or expired)
    if (isInIframe) {
      console.warn("401 Unauthorized - Static token authentication failed in iframe");
      // Don't redirect or clear localStorage when in iframe
      return result;
    }

    // Otherwise, use normal 401 handling with token refresh
    // console.warn("401 Unauthorized - attempting to refresh session");
    addBreadcrumb({
      message: '401 Unauthorized - attempting token refresh',
      category: 'auth',
      level: 'warning',
      data: {
        url: typeof args === 'string' ? args : args.url,
      },
    });

    try {
      // Priority 1: Try JWT token refresh first
      const session: any = await getSession(); // this may already refresh session internally
      const token = session?.idToken?.jwtToken;

      if (token) {
        // Always use "Bearer" for JWT tokens
        if (typeof args === "object") {
          args.headers = {
            ...(args.headers || {}),
            Authorization: `Bearer ${token}`,
          };
        }

        const retryResult = await baseQuery(args, api, extraOptions);
        return retryResult;
      }
    } catch (error) {
      // JWT refresh failed, try Shopify tokens
      const urlParams = new URLSearchParams(window.location.search);
      const shopifyTokenFromUrl = urlParams.get("token")?.replace(/ /g, '+');
      const emailShop = urlParams.get("email");
      const getShopifyUser = localStorage.getItem("oogw.ai_user");
      const storedTokenType = localStorage.getItem("oogw.ai_user_type");

      // Priority 2: Shopify token from URL
      if (shopifyTokenFromUrl && emailShop) {
        if (typeof args === "object") {
          args.headers = {
            ...(args.headers || {}),
            Authorization: `Shopify ${shopifyTokenFromUrl}`,
          };
        }
        const retryResult = await baseQuery(args, api, extraOptions);
        return retryResult;
      }
      // Priority 3: Shopify token from localStorage
      else if (storedTokenType === "Shopify" && getShopifyUser) {
        if (typeof args === "object") {
          args.headers = {
            ...(args.headers || {}),
            Authorization: `Shopify ${getShopifyUser}`,
          };
        }
        const retryResult = await baseQuery(args, api, extraOptions);
        return retryResult;
      }
    }

    // If we get here, all token attempts failed - redirect to login
    // console.error("Failed to refresh session after 401");
    addBreadcrumb({
      message: 'All token refresh attempts failed after 401',
      category: 'auth',
      level: 'error',
    });
    captureException(new Error('All authentication tokens failed'), { context: 'session_refresh_401' });
    localStorage.clear();
    toast.error("Session expired. Please log in again.", {
      toastId: "session-expired",
    });
    window.location.href = "/login";
  }

  // Handle fetch errors and capture via Sentry
  if (result?.error) {
    // Check for parsing errors (including HTML responses)
    if (result.error.status === 'PARSING_ERROR') {
      console.error('Parsing error:', result.error);
      toast.error("Something went wrong. Please try again.", {
        toastId: "parsing-error",
      });
    }

    addBreadcrumb({
      message: 'API request failed',
      category: 'api',
      level: 'error',
      data: {
        url: typeof args === 'string' ? args : args.url,
        status: result.error.status,
        error: result.error,
      },
    });

    // Convert RTK Query error object to proper Error instance for Sentry
    const errorMessage = (result.error as any)?.data?.message
      || (result.error as any)?.data?.error
      || (result.error as any)?.error
      || `API Error: ${result.error.status}`;
    const error = new Error(errorMessage);
    error.name = 'RTKQueryError';
    
    captureException(error, {
      url: typeof args === 'string' ? args : args.url,
      status: result.error.status,
      errorData: result.error,
    });
    if (
      result.error.status === "FETCH_ERROR" && !navigator.onLine
    ) {
      api.dispatch(updateOnlineStatus(false));
      // Optionally redirect to an error page
      // window.location.href = "/error";
    } else if (result.error.status === "FETCH_ERROR" && navigator.onLine) {
      api.dispatch(updateOnlineStatus(true));
    }
  }

  return result;
};

// API Slice
export const baseApi = createApi({
  reducerPath: "baseApiReducer",
  baseQuery: customFetchBaseQuery,
  endpoints: () => ({}),
  tagTypes: [
    "generalSettings",
    "notificationSettings",
    "metaIntegration",
    "teams",
    "billing",
    "dashboard",
    "useHistory",
    "conversationHistory",
    "createProfile",
    "currentPlan",
    "customIntegration",
    "integrationList",
    "integrationStatus",
    "integrationErrors",
    "defaultProperties",
    "workspace-integration",
    "integration-status",
    "auditLogs",
  ],
});
